import type {
  PartnerAnwender,
  PartnerGeraet,
  PartnerKopplungscode,
  PartnerKunde,
  PartnerRecht,
  PartnerZugangslink,
} from '@nac2s/shared'
import { type ReactNode, useCallback, useEffect, useId, useState } from 'react'
import { Link, useParams } from 'react-router'
import { Dialog, Modal, ModalOverlay } from '@/components/application/modals/modal'
import { Toggle } from '@/components/base/toggle/toggle'
import { Dialogfenster } from '@/teile/Dialogfenster'
import { Knopf } from '@/teile/Knopf'
import { Reiter } from '@/teile/Reiter'
import { Status } from '@/teile/Status'
import { Fehler, Laedt, Leer, Tabelle } from '@/teile/Tabelle'
import { cx } from '@/utils/cx'
import { ApiError } from '../api/client.js'
import {
  anwenderAendern,
  anwenderAnlegen,
  kopplungscodeAusgeben,
  ladeAnwender,
  ladeGeraete,
  ladeKunden,
  zugangslinkAusstellen,
} from '../api/partner.js'
import { useSitzung } from '../session/SitzungContext.js'
import { Kundeneinstellungen } from '../teile/Kundeneinstellungen.js'
import { SipgatePushApi } from '../teile/SipgatePushApi.js'

/**
 * Ein Kunde — eigene Seite, eigene Adresse.
 *
 * Bewusst keine Lade und kein Aufklappen in der Liste: Der Partner bleibt
 * hier länger, und eine Adresse lässt sich verlinken und weitergeben. Der
 * Zurückweg steht oben, nicht nur in der Browserleiste.
 *
 * **Was nirgends erscheint** (`Projektstruktur.md` §3, §7): Rufnummern,
 * Verlauf, Zusammenfassungen, der sipgate-Token oder seine Metadaten. Vom
 * Token bleibt ein Bit: ob einer hinterlegt ist. Die Gerätekennung kommt
 * gekürzt — sie reicht zum Wiedererkennen im Gespräch und zu nichts sonst.
 */

type Ladezustand =
  | { status: 'laedt' }
  | { status: 'da'; kunde: PartnerKunde }
  | { status: 'fehlt' }
  | { status: 'fehler'; meldung: string }

type Register = 'anwender' | 'geraete' | 'einstellungen' | 'push'

export function Kunde() {
  const { host = '' } = useParams<{ host: string }>()
  const [zustand, setZustand] = useState<Ladezustand>({ status: 'laedt' })
  const [register, setRegister] = useState<Register>('anwender')
  const { zustand: sitzung } = useSitzung()
  const rechte = sitzung.status === 'angemeldet' ? sitzung.sitzung.rechte : []
  const darfTechnik = rechte.includes('kunden.technik')
  const [formularOffen, setFormularOffen] = useState(false)
  // Der Kopplungscode erscheint genau einmal — auf diesem Zettel, sonst nirgends.
  const [codezettel, setCodezettel] = useState<Codezettel | null>(null)
  // Zaehlt hoch, wenn ein Anwender angelegt wurde: Die Liste im Register
  // laedt dann neu, ohne dass die Seite selbst die Liste kennen muss.
  const [listeStand, setListeStand] = useState(0)

  /**
   * Laedt den Kunden. Nach einer Aenderung zeigt die Seite sonst alte Werte.
   *
   * Als Funktion und nicht als Zaehler: Ein Zaehler waere eine Abhaengigkeit,
   * die nur dazu da ist, eine andere auszuloesen — und genau daran erkennt
   * die Regel `useExhaustiveDependencies` zu Recht etwas Erfundenes.
   */
  const neuLaden = useCallback(() => {
    let abgebrochen = false
    ladeKunden()
      .then((kunden) => {
        if (abgebrochen) return
        const treffer = kunden.find((k) => k.host === host)
        setZustand(treffer ? { status: 'da', kunde: treffer } : { status: 'fehlt' })
      })
      .catch((fehler: unknown) => {
        if (abgebrochen) return
        setZustand({
          status: 'fehler',
          meldung: fehler instanceof ApiError ? fehler.message : 'Unbekannter Fehler',
        })
      })
    return () => {
      abgebrochen = true
    }
  }, [host])

  useEffect(() => neuLaden(), [neuLaden])

  if (zustand.status === 'laedt') {
    return (
      <p role="status" className="px-6 py-10 text-sm text-tertiary">
        Kunde wird geladen …
      </p>
    )
  }

  if (zustand.status === 'fehler' || zustand.status === 'fehlt') {
    return (
      <div className="px-6 py-8">
        <Zurueck />
        <div className="mt-4 rounded-xl border border-secondary bg-primary p-10 text-center">
          <p className="font-medium text-primary">
            {zustand.status === 'fehlt' ? 'Diesen Kunden gibt es nicht' : 'Nicht erreichbar'}
          </p>
          <p className="mt-1 text-sm text-tertiary">
            {zustand.status === 'fehlt'
              ? 'Vielleicht wurde er gelöscht oder die Adresse stimmt nicht.'
              : zustand.meldung}
          </p>
        </div>
      </div>
    )
  }

  const { kunde } = zustand
  const aktiv = kunde.zustand === 'aktiv'

  return (
    <div>
      <Zurueck />

      <div className="flex flex-wrap items-start gap-3.5 px-6 pt-2.5">
        <span
          aria-hidden="true"
          className={cx(
            'grid size-11 place-items-center rounded-[14px] font-display text-base font-bold tracking-tight',
            zeichenFarbe(kunde),
          )}
        >
          {kuerzel(kunde.anzeigename)}
        </span>
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-[26px] leading-[1.1] font-extrabold tracking-[-0.025em] text-primary">
              {kunde.anzeigename}
            </h1>
            <Zustandsmarke kunde={kunde} />
            {kunde.istDemo ? <Status ton="ruhig">Demo</Status> : null}
          </div>
          <p className="mt-0.5 text-[13px] text-quaternary">
            {kunde.host} ·{' '}
            {aktiv && kunde.aktivSeit
              ? `aktiv seit ${datum(kunde.aktivSeit)}`
              : kunde.zustand === 'bereitstellung'
                ? 'wird bereitgestellt'
                : 'deaktiviert'}
          </p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          {aktiv ? (
            <>
              <Knopf onClick={() => setRegister('einstellungen')}>Einstellungen</Knopf>
              <Knopf stark disabled={!darfTechnik} onClick={() => setFormularOffen(true)}>
                Anwender anlegen
              </Knopf>
            </>
          ) : kunde.zustand === 'bereitstellung' ? (
            <>
              <Knopf>Auftrag ansehen</Knopf>
              <Knopf stark>Freigeben</Knopf>
            </>
          ) : (
            <Knopf stark>Wieder aktivieren</Knopf>
          )}
        </div>
      </div>

      <div className="mt-[18px] flex gap-5 border-b border-secondary px-6" role="tablist">
        {aktiv ? (
          <>
            <Reiter an={register === 'anwender'} onWahl={() => setRegister('anwender')}>
              Anwender
            </Reiter>
            <Reiter an={register === 'geraete'} onWahl={() => setRegister('geraete')}>
              Geräte
            </Reiter>
          </>
        ) : null}
        <Reiter an={register === 'einstellungen'} onWahl={() => setRegister('einstellungen')}>
          Einstellungen
        </Reiter>
        <Reiter an={register === 'push'} onWahl={() => setRegister('push')}>
          Sipgate Push-API
        </Reiter>
      </div>

      {register === 'einstellungen' ? (
        <div className="px-6 pt-[18px] pb-7">
          <Kundeneinstellungen kunde={kunde} rechte={rechte} onGeaendert={neuLaden} />
        </div>
      ) : register === 'push' ? (
        <div className="px-6 pt-[18px] pb-7">
          <SipgatePushApi host={kunde.host} darfTechnik={darfTechnik} />
        </div>
      ) : aktiv ? (
        <>
          <dl className="flex flex-wrap gap-[30px] px-6 pt-[18px]">
            <Wert name="Seeds">
              <span className="tabular-nums">
                {kunde.seatsBelegt ?? '—'}{' '}
                <small className="text-base font-normal text-tertiary">
                  / {kunde.seatCap === null ? '∞' : kunde.seatCap}
                </small>
              </span>
            </Wert>
            <Wert name="Geräte">
              <span className="tabular-nums">{kunde.geraete ?? '—'}</span>
            </Wert>
            <Wert name="Ohne sipgate-Zugang" achtung={Boolean(kunde.anwenderOhneZugang)}>
              <span className="tabular-nums">{kunde.anwenderOhneZugang ?? '—'}</span>
            </Wert>
            <Wert name="Ohne Zeichen seit 30 Tagen" achtung={Boolean(kunde.geraeteOhneZeichen)}>
              <span className="tabular-nums">{kunde.geraeteOhneZeichen ?? '—'}</span>
            </Wert>
          </dl>

          <div className="px-6 pt-[18px] pb-7">
            {codezettel ? (
              <Codezettel zettel={codezettel} onZu={() => setCodezettel(null)} />
            ) : null}
            {register === 'anwender' ? (
              <AnwenderListe
                kunde={kunde}
                rechte={rechte}
                stand={listeStand}
                onCode={setCodezettel}
              />
            ) : (
              <GeraeteListe host={host} />
            )}
          </div>
          {formularOffen ? (
            <AnwenderAnlegenModal
              host={kunde.host}
              onFertig={(zettel) => {
                setFormularOffen(false)
                setCodezettel(zettel)
                setListeStand((n) => n + 1)
                setRegister('anwender')
              }}
              onAbbruch={() => setFormularOffen(false)}
            />
          ) : null}
        </>
      ) : (
        <div className="mx-6 mt-[18px] mb-7 rounded-xl border border-dashed border-secondary p-8 text-center">
          <p className="font-medium text-primary">
            {kunde.zustand === 'bereitstellung'
              ? 'Noch keine Anwender'
              : 'Deaktiviert, nicht gelöscht'}
          </p>
          <p className="mx-auto mt-1 max-w-md text-sm text-tertiary">
            {kunde.zustand === 'bereitstellung'
              ? 'Datenbank und Schlüssel sind angelegt. Sobald der Auftrag freigegeben ist, wird der Kunde erreichbar — die Abrechnung beginnt erst dann, nicht mit dem Anlegen.'
              : 'Der Host antwortet wie ein unbekannter, die Daten liegen weiter. Nach Ablauf der Frist werden sie entfernt.'}
          </p>
        </div>
      )}
    </div>
  )
}

/**
 * Lädt eine Liste und hält die drei Zustände auseinander.
 *
 * Zusammengefasst, weil beide Register dasselbe brauchen — und weil sonst
 * beim zweiten Register wieder „Wird geladen" ohne Fehlerfall entstünde.
 *
 * Gibt das Nachladen als Funktion heraus, nicht als Zähler. Ein Zähler wäre
 * eine Abhängigkeit, die nur dazu da ist, eine andere auszulösen — und genau
 * daran erkennt `useExhaustiveDependencies` zu Recht etwas Erfundenes. Dieselbe
 * Bauform wie `neuLaden` weiter oben.
 *
 * `zeigeLadezustand` trennt die beiden Anlässe: Beim Wechsel des Kunden gehört
 * „Wird geladen" hin, denn die alten Zeilen gehören jemand anderem. Nach einer
 * eigenen Änderung nicht — dort verschwände die Tabelle für einen Wimpernschlag
 * und käme fast unverändert zurück.
 */
function useListe<T>(laden: (host: string) => Promise<T[]>, host: string, stand = 0) {
  const [zustand, setZustand] = useState<
    { status: 'laedt' } | { status: 'da'; zeilen: T[] } | { status: 'fehler'; meldung: string }
  >({ status: 'laedt' })

  // `stand` steht in den Abhaengigkeiten, weil eine Aenderung von aussen ein
  // Neuladen ist — die eine Abhaengigkeit, die nicht aus den Argumenten folgt.
  // biome-ignore lint/correctness/useExhaustiveDependencies: stand ist das Signal zum Neuladen
  const holen = useCallback(
    (zeigeLadezustand: boolean) => {
      let abgebrochen = false
      if (zeigeLadezustand) setZustand({ status: 'laedt' })
      laden(host)
        .then((zeilen) => {
          if (!abgebrochen) setZustand({ status: 'da', zeilen })
        })
        .catch((fehler: unknown) => {
          if (abgebrochen) return
          setZustand({
            status: 'fehler',
            meldung: fehler instanceof ApiError ? fehler.message : 'Unbekannter Fehler',
          })
        })
      return () => {
        abgebrochen = true
      }
    },
    [laden, host, stand],
  )

  useEffect(() => holen(true), [holen])

  return [zustand, () => holen(false)] as const
}

/** Was die Stufe des Kunden für den einzelnen Anwender bedeutet. */
const STUFENSATZ: Record<string, string> = {
  keine:
    'Bei diesem Kunden ist Selbstverwaltung abgeschaltet — die Stufe steht in den Einstellungen.',
  geraete: 'Wer sie hat, gibt Kopplungscodes aus und sperrt Geräte.',
  anwender_und_geraete: 'Wer sie hat, legt zusätzlich Anwender an und deaktiviert sie.',
}

/**
 * Anwender des Kunden — und wer dort selbst verwalten darf.
 *
 * **Die zweite Hälfte der Selbstverwaltung.** Die Stufe in den Einstellungen
 * sagt, *was* bei diesem Kunden geht; hier steht, *wer* es tut
 * (`Projektstruktur.md` §3.1). Mit nur einer der beiden Hälften passiert
 * nichts — deshalb steht der Satz zur Stufe über der Liste und nicht
 * irgendwo, wo man ihn suchen müsste.
 *
 * Bei Stufe „keine" bleibt der Schalter **sichtbar und gesperrt** statt weg:
 * Wer ihn sucht und nicht findet, hält das Merkmal für nicht vorhanden.
 */
function AnwenderListe({
  kunde,
  rechte,
  stand,
  onCode,
}: {
  kunde: PartnerKunde
  rechte: readonly PartnerRecht[]
  stand: number
  onCode: (zettel: Codezettel) => void
}) {
  const [zustand, neuLaden] = useListe<PartnerAnwender>(ladeAnwender, kunde.host, stand)
  const [arbeitetAn, setArbeitetAn] = useState<string | null>(null)
  const [zugang, setZugang] = useState<Zugangszettel | null>(null)
  const [adresseVon, setAdresseVon] = useState<PartnerAnwender | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)

  const darfTechnik = rechte.includes('kunden.technik')
  const stufeAus = kunde.adminLightStufe === 'keine'

  async function umschalten(id: string, an: boolean) {
    setArbeitetAn(id)
    setMeldung(null)
    try {
      await anwenderAendern(kunde.host, id, { selbstverwaltung: an })
      neuLaden()
    } catch (fehler: unknown) {
      setMeldung(
        fehler instanceof ApiError && fehler.status === 403
          ? 'Ihre Rolle darf die Selbstverwaltung nicht vergeben.'
          : 'Die Änderung ist nicht angekommen.',
      )
    } finally {
      setArbeitetAn(null)
    }
  }

  async function sperren(id: string, aktiv: boolean) {
    setArbeitetAn(id)
    setMeldung(null)
    try {
      await anwenderAendern(kunde.host, id, { aktiv })
      neuLaden()
    } catch (fehler: unknown) {
      setMeldung(
        fehler instanceof ApiError && fehler.status === 403
          ? 'Ihre Rolle darf Anwender nicht sperren.'
          : 'Die Änderung ist nicht angekommen.',
      )
    } finally {
      setArbeitetAn(null)
    }
  }

  async function kontaktpflegeUmschalten(id: string, an: boolean) {
    setArbeitetAn(id)
    setMeldung(null)
    try {
      await anwenderAendern(kunde.host, id, { kontaktpflege: an })
      neuLaden()
    } catch (fehler: unknown) {
      setMeldung(
        fehler instanceof ApiError && fehler.status === 403
          ? 'Ihre Rolle darf das nicht ändern.'
          : 'Die Änderung ist nicht angekommen.',
      )
    } finally {
      setArbeitetAn(null)
    }
  }

  async function adresseSetzen(a: PartnerAnwender, email: string | null) {
    setArbeitetAn(a.id)
    setMeldung(null)
    try {
      await anwenderAendern(kunde.host, a.id, { email })
      setAdresseVon(null)
      neuLaden()
    } catch (fehler: unknown) {
      setMeldung(
        fehler instanceof ApiError && fehler.code === 'email_belegt'
          ? 'Diese Adresse hat bei diesem Kunden schon jemand.'
          : fehler instanceof ApiError && fehler.status === 403
            ? 'Ihre Rolle darf Anwender nicht ändern.'
            : 'Die Adresse ist nicht angekommen.',
      )
    } finally {
      setArbeitetAn(null)
    }
  }

  async function neuerZugang(a: PartnerAnwender) {
    setArbeitetAn(a.id)
    setMeldung(null)
    try {
      setZugang({ name: a.anzeigename, ...(await zugangslinkAusstellen(kunde.host, a.id)) })
    } catch (fehler: unknown) {
      setMeldung(
        fehler instanceof ApiError && fehler.code === 'anwender_ohne_email'
          ? 'Dieser Anwender hat noch keine E-Mail-Adresse — ohne sie gibt es nichts, womit er sich anmelden könnte.'
          : fehler instanceof ApiError && fehler.code === 'zweiter_faktor_noetig'
            ? 'Selbstverwalter brauchen einen zweiten Faktor. Solange es den nicht gibt, kommen sie nur über die App herein.'
            : fehler instanceof ApiError && fehler.status === 403
              ? 'Ihre Rolle darf keine Zugangslinks ausstellen.'
              : 'Der Link konnte nicht erzeugt werden.',
      )
    } finally {
      setArbeitetAn(null)
    }
  }

  async function neuerCode(a: PartnerAnwender) {
    setArbeitetAn(a.id)
    setMeldung(null)
    try {
      const code = await kopplungscodeAusgeben(kunde.host, a.id)
      onCode({ name: a.anzeigename, ...code })
    } catch (fehler: unknown) {
      setMeldung(
        fehler instanceof ApiError && fehler.status === 403
          ? 'Ihre Rolle darf keine Kopplungscodes ausgeben.'
          : 'Der Code konnte nicht erzeugt werden.',
      )
    } finally {
      setArbeitetAn(null)
    }
  }

  if (zustand.status === 'laedt') return <Laedt>Anwender werden geladen …</Laedt>
  if (zustand.status === 'fehler') return <Fehler>{zustand.meldung}</Fehler>
  if (zustand.zeilen.length === 0) {
    return (
      <Leer titel="Noch keine Anwender">Legen Sie den ersten Anwender für diesen Kunden an.</Leer>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[12.5px] text-tertiary">
        {STUFENSATZ[kunde.adminLightStufe] ?? STUFENSATZ.keine}
      </p>

      {meldung ? <Fehler>{meldung}</Fehler> : null}

      {zugang ? <Zugangszettel zettel={zugang} onZu={() => setZugang(null)} /> : null}

      {adresseVon ? (
        <Adressdialog
          anwender={adresseVon}
          laeuft={arbeitetAn === adresseVon.id}
          onSpeichern={(email) => void adresseSetzen(adresseVon, email)}
          onAbbruch={() => setAdresseVon(null)}
        />
      ) : null}

      <Tabelle
        kopf={[
          'Anwender',
          'Anmeldung',
          'Geräte',
          'sipgate-Zugang',
          'Selbstverwaltung',
          'Kontaktpflege',
          'Angelegt',
          '',
        ]}
      >
        {zustand.zeilen.map((a) => (
          <tr key={a.id} className="border-b border-secondary last:border-b-0">
            <td className="py-3 pr-4">
              <span className="flex items-center gap-2.5">
                <span className="grid size-7 shrink-0 place-items-center rounded-full bg-secondary text-xs font-semibold text-secondary">
                  {kuerzel(a.anzeigename)}
                </span>
                <span className={cx('font-medium', a.aktiv ? 'text-primary' : 'text-tertiary')}>
                  {a.anzeigename}
                </span>
                {a.aktiv ? null : <Status ton="ruhig">deaktiviert</Status>}
              </span>
            </td>
            <td className="py-3 pr-4 text-sm">
              {a.email ? (
                <span className="flex flex-col items-start gap-0.5">
                  <button
                    type="button"
                    disabled={!darfTechnik}
                    onClick={() => setAdresseVon(a)}
                    className="cursor-pointer text-tertiary underline-offset-2 hover:underline disabled:cursor-default disabled:no-underline"
                  >
                    {a.email}
                  </button>
                  {a.hatPasswort ? (
                    <Status ton="gut">Passwort gesetzt</Status>
                  ) : (
                    <Status ton="wartet">kein Passwort</Status>
                  )}
                </span>
              ) : darfTechnik ? (
                <Knopf onClick={() => setAdresseVon(a)}>E-Mail setzen</Knopf>
              ) : (
                <span className="text-quaternary">—</span>
              )}
            </td>
            <td className="py-3 pr-4 text-sm tabular-nums">
              <span className="font-medium text-primary">{a.geraete}</span>
              <span className="text-tertiary"> / {kunde.geraeteJeAnwender}</span>
            </td>
            <td className="py-3 pr-4">
              {a.sipgateZugang ? (
                <Status ton="gut">hinterlegt</Status>
              ) : (
                <Status ton="wartet">fehlt noch</Status>
              )}
            </td>
            <td className="py-3 pr-4">
              <Toggle
                size="sm"
                aria-label={`Selbstverwaltung für ${a.anzeigename}`}
                // Ist sie am Unternehmen aus, ist sie fuer jeden aus — der
                // Schalter zeigt den Zustand, der gilt, und nicht eine
                // Vergabe, die nichts bewirkt (ADR-0017).
                isSelected={!stufeAus && a.selbstverwaltung}
                isDisabled={!darfTechnik || stufeAus || arbeitetAn === a.id}
                onChange={(an) => void umschalten(a.id, an)}
              />
            </td>
            <td className="py-3 pr-4">
              <Toggle
                size="sm"
                aria-label={`Zentrale Kontakte pflegen für ${a.anzeigename}`}
                isSelected={a.kontaktpflege}
                isDisabled={!darfTechnik || arbeitetAn === a.id}
                onChange={(an) => void kontaktpflegeUmschalten(a.id, an)}
              />
            </td>
            <td className="py-3 pr-4 text-sm text-tertiary tabular-nums">{datum(a.angelegtAm)}</td>
            <td className="py-3 text-right">
              {darfTechnik ? (
                <span className="inline-flex gap-1.5">
                  {a.aktiv ? (
                    <Knopf disabled={arbeitetAn === a.id} onClick={() => void neuerCode(a)}>
                      Kopplungscode
                    </Knopf>
                  ) : null}
                  {a.aktiv && a.email ? (
                    <Knopf disabled={arbeitetAn === a.id} onClick={() => void neuerZugang(a)}>
                      Zugangslink
                    </Knopf>
                  ) : null}
                  {a.aktiv ? (
                    <Knopf
                      gefahr
                      disabled={arbeitetAn === a.id}
                      onClick={() => void sperren(a.id, false)}
                    >
                      Sperren
                    </Knopf>
                  ) : (
                    <Knopf disabled={arbeitetAn === a.id} onClick={() => void sperren(a.id, true)}>
                      Entsperren
                    </Knopf>
                  )}
                </span>
              ) : null}
            </td>
          </tr>
        ))}
      </Tabelle>
    </div>
  )
}

/**
 * Die Adresse eines Anwenders setzen oder wieder wegnehmen.
 *
 * Sie ist die Kennung für die Anmeldung im Browser (ADR-0004 Nachtrag 5).
 * Ohne sie kommt der Anwender nur über die App herein — das ist ein gültiger
 * Zustand und kein Mangel, deshalb lässt sich die Adresse auch leeren.
 */
function Adressdialog({
  anwender,
  laeuft,
  onSpeichern,
  onAbbruch,
}: {
  anwender: PartnerAnwender
  laeuft: boolean
  onSpeichern: (email: string | null) => void
  onAbbruch: () => void
}) {
  const [wert, setWert] = useState(anwender.email ?? '')
  const getrimmt = wert.trim()
  const gueltig = getrimmt === '' || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(getrimmt)
  return (
    <Dialogfenster titel={`E-Mail für ${anwender.anzeigename}`} onSchliessen={onAbbruch}>
      <form
        className="flex flex-col gap-4 pb-6"
        onSubmit={(e) => {
          e.preventDefault()
          if (!gueltig || laeuft) return
          onSpeichern(getrimmt === '' ? null : getrimmt.toLowerCase())
        }}
      >
        <label className="flex flex-col gap-1.5">
          <span className="text-[11.5px] font-medium text-quaternary">E-Mail</span>
          <input
            type="email"
            value={wert}
            // biome-ignore lint/a11y/noAutofocus: Der Dialog hat genau ein Feld.
            autoFocus
            onChange={(e) => setWert(e.target.value)}
            className="rounded-[10px] border border-secondary bg-primary px-3 py-2 text-sm text-primary outline-none focus:border-rand-aktiv"
          />
        </label>
        {!gueltig ? <Fehler>Das sieht nicht nach einer E-Mail-Adresse aus.</Fehler> : null}
        {anwender.hatPasswort && getrimmt === '' ? (
          <Fehler>
            Dieser Anwender hat ein Passwort. Ohne Adresse kann er sich damit nicht mehr anmelden.
          </Fehler>
        ) : null}
        <div className="flex items-center justify-end gap-2.5 border-t border-secondary pt-4">
          <Knopf onClick={onAbbruch} disabled={laeuft}>
            Abbrechen
          </Knopf>
          <Knopf type="submit" stark disabled={!gueltig || laeuft}>
            {laeuft ? 'Wird gespeichert …' : 'Speichern'}
          </Knopf>
        </div>
      </form>
    </Dialogfenster>
  )
}

/** Was auf dem Zettel steht: Name des Anwenders und sein Link — einmalig. */
interface Zugangszettel extends PartnerZugangslink {
  name: string
}

/**
 * Der Zugangslink, genau einmal.
 *
 * Dieselbe Bauform wie der Kopplungscode. Es gibt keinen Mailversand: Der
 * Partner kopiert den Link und gibt ihn weiter. Die Datenbank kennt nur seinen
 * Hash — wer ihn verliert, stellt einen neuen aus, und der entwertet den alten.
 */
function Zugangszettel({ zettel, onZu }: { zettel: Zugangszettel; onZu: () => void }) {
  const [kopiert, setKopiert] = useState(false)
  return (
    <div className="mb-4 rounded-xl border border-achtung bg-achtung-flaeche p-4">
      <p className="text-[13px] font-semibold text-achtung-tief">Zugangslink für {zettel.name}</p>
      <p className="mt-1 text-[12.5px] text-achtung-tief">
        Erscheint <strong>nur jetzt</strong>. Gilt bis {datum(zettel.gueltigBis)} und nur einmal.
        Damit setzt {zettel.name} ein Passwort und meldet sich danach von jedem Gerät an.
      </p>
      <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
        <code className="max-w-full truncate rounded-lg border border-secondary bg-primary px-3 py-2 font-mono text-[13px] text-primary select-all">
          {zettel.url}
        </code>
        <Knopf
          onClick={() => {
            void navigator.clipboard?.writeText(zettel.url).then(() => setKopiert(true))
          }}
        >
          {kopiert ? 'Kopiert' : 'Kopieren'}
        </Knopf>
        <Knopf onClick={onZu}>Schließen</Knopf>
      </div>
    </div>
  )
}

/** Was auf dem Zettel steht: Name des Anwenders und sein Code — einmalig. */
interface Codezettel extends PartnerKopplungscode {
  name: string
}

/**
 * Der Kopplungscode, genau einmal.
 *
 * Dieselbe Bauform wie der Kennwortzettel der Konten: Flaeche in Achtung,
 * Code zum Markieren und Kopieren, ein Satz dazu, was damit zu tun ist. Der
 * Code steht nirgends sonst — die Datenbank kennt nur seinen Hash (ADR-0004).
 */
function Codezettel({ zettel, onZu }: { zettel: Codezettel; onZu: () => void }) {
  const [kopiert, setKopiert] = useState(false)
  return (
    <div className="mb-4 rounded-xl border border-achtung bg-achtung-flaeche p-4">
      <p className="text-[13px] font-semibold text-achtung-tief">Kopplungscode für {zettel.name}</p>
      <p className="mt-1 text-[12.5px] text-achtung-tief">
        Erscheint <strong>nur jetzt</strong>. Gilt bis {datum(zettel.gueltigBis)} für{' '}
        {zettel.einloesungen} Geräte. In der App: Rechtsklick auf das Symbol → „Gerät registrieren
        …".
      </p>
      <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
        <code className="rounded-lg border border-secondary bg-primary px-3 py-2 font-mono text-[13px] text-primary select-all">
          {zettel.code}
        </code>
        <Knopf
          onClick={() => {
            void navigator.clipboard?.writeText(zettel.code).then(() => setKopiert(true))
          }}
        >
          {kopiert ? 'Kopiert' : 'Kopieren'}
        </Knopf>
        <Knopf onClick={onZu}>Schließen</Knopf>
      </div>
    </div>
  )
}

/**
 * Anwender anlegen — im Modal, ein Feld.
 *
 * Nach dem Anlegen wird sofort ein Kopplungscode erzeugt: Der Grund, jemanden
 * anzulegen, ist fast immer, dass er seinen Arbeitsplatz koppeln soll. Wer
 * spaeter einen weiteren braucht, holt ihn aus der Liste.
 */
function AnwenderAnlegenModal({
  host,
  onFertig,
  onAbbruch,
}: {
  host: string
  onFertig: (zettel: Codezettel) => void
  onAbbruch: () => void
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const titelId = useId()
  // Die Adresse ist freiwillig: Wer nur mit der App arbeitet, braucht keine.
  // Wenn eine da steht, muss sie aber eine sein.
  const emailOk = email.trim() === '' || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())
  const vollstaendig = name.trim().length > 0 && emailOk

  return (
    <ModalOverlay
      isOpen
      isDismissable={!laeuft}
      onOpenChange={(offen) => {
        if (!offen) onAbbruch()
      }}
    >
      <Modal className="w-full max-w-[28rem]">
        <Dialog aria-labelledby={titelId}>
          <form
            className="flex flex-col gap-5 p-6"
            onSubmit={(e) => {
              e.preventDefault()
              if (!vollstaendig || laeuft) return
              setLaeuft(true)
              setMeldung(null)
              anwenderAnlegen(host, {
                anzeigename: name.trim(),
                ...(email.trim() ? { email: email.trim().toLowerCase() } : {}),
              })
                .then(async (a) => {
                  const code = await kopplungscodeAusgeben(host, a.id)
                  onFertig({ name: a.anzeigename, ...code })
                })
                .catch((fehler: unknown) =>
                  setMeldung(
                    fehler instanceof ApiError ? fehler.message : 'Anlegen fehlgeschlagen.',
                  ),
                )
                .finally(() => setLaeuft(false))
            }}
          >
            <div>
              <h2 id={titelId} className="font-display text-lg font-bold text-primary">
                Anwender anlegen
              </h2>
              <p className="mt-1 text-[13px] text-tertiary">
                Der Kopplungscode erscheint <strong>einmalig</strong>, sobald der Anwender steht.
              </p>
            </div>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11.5px] font-medium text-quaternary">Name</span>
              <input
                type="text"
                value={name}
                placeholder="Vor- und Nachname"
                autoFocus
                onChange={(e) => setName(e.target.value)}
                className="rounded-[10px] border border-secondary bg-primary px-3 py-2 text-sm text-primary outline-none focus:border-rand-aktiv"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11.5px] font-medium text-quaternary">E-Mail (optional)</span>
              <input
                type="email"
                value={email}
                placeholder="optional"
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-[10px] border border-secondary bg-primary px-3 py-2 text-sm text-primary outline-none focus:border-rand-aktiv"
              />
            </label>
            {!emailOk ? <Fehler>Das sieht nicht nach einer E-Mail-Adresse aus.</Fehler> : null}
            {meldung ? <Fehler>{meldung}</Fehler> : null}
            <div className="flex items-center justify-end gap-2.5 border-t border-secondary pt-4">
              <Knopf onClick={onAbbruch} disabled={laeuft}>
                Abbrechen
              </Knopf>
              <Knopf type="submit" stark disabled={!vollstaendig || laeuft}>
                {laeuft ? 'Wird angelegt …' : 'Anlegen'}
              </Knopf>
            </div>
          </form>
        </Dialog>
      </Modal>
    </ModalOverlay>
  )
}

function GeraeteListe({ host }: { host: string }) {
  const [zustand] = useListe<PartnerGeraet>(ladeGeraete, host)

  if (zustand.status === 'laedt') return <Laedt>Geräte werden geladen …</Laedt>
  if (zustand.status === 'fehler') return <Fehler>{zustand.meldung}</Fehler>
  if (zustand.zeilen.length === 0) {
    return (
      <Leer titel="Noch keine Geräte">
        Geräte entstehen, wenn ein Anwender einen Kopplungscode einlöst.
      </Leer>
    )
  }

  return (
    <Tabelle kopf={['Gerät', 'Anwender', 'Zuletzt gesehen', 'Gekoppelt', 'Zustand']}>
      {zustand.zeilen.map((g) => (
        <tr key={g.kennung} className="border-b border-secondary last:border-b-0">
          <td className="py-3 pr-4">
            <span className="block font-medium text-primary">{g.bezeichnung}</span>
            <span className="block font-mono text-xs text-tertiary">{g.kennung}</span>
          </td>
          <td className="py-3 pr-4 text-sm text-tertiary">{g.anwender}</td>
          <td className="py-3 pr-4 text-sm tabular-nums">
            {g.zuletztGesehen ? (
              <span className={g.ohneZeichen ? 'text-achtung-tief' : 'text-tertiary'}>
                {datum(g.zuletztGesehen)}
              </span>
            ) : (
              <span className="text-achtung-tief">nie</span>
            )}
          </td>
          <td className="py-3 pr-4 text-sm text-tertiary tabular-nums">
            {g.gekoppeltAm ? datum(g.gekoppeltAm) : '—'}
          </td>
          <td className="py-3">
            <Status
              ton={g.zustand === 'active' ? 'gut' : g.zustand === 'pending' ? 'wartet' : 'ruhig'}
            >
              {g.zustand === 'active' ? 'aktiv' : g.zustand === 'pending' ? 'wartet' : 'widerrufen'}
            </Status>
          </td>
        </tr>
      ))}
    </Tabelle>
  )
}

function Zurueck() {
  return (
    <Link
      to="/"
      className="mt-[18px] ml-6 inline-flex w-fit items-center gap-[7px] text-[13px] text-tertiary hover:text-primary"
    >
      {/* Ein Winkel, kein Textpfeil: In der Vorlage ist es eine Linie mit
          derselben Staerke wie der Text daneben. Das Zeichen „←" bringt
          seine eigene Strichstaerke mit und wirkt daneben schwer. */}
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="size-3.5">
        <path
          d="M10 3L5 8l5 5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Alle Kunden
    </Link>
  )
}

function zeichenFarbe(kunde: PartnerKunde): string {
  if (kunde.zustand === 'deaktiviert') return 'bg-secondary text-tertiary'
  if (kunde.zustand === 'bereitstellung') return 'bg-achtung-flaeche text-achtung-tief'
  return 'bg-success-primary text-fg-success-primary'
}

function Zustandsmarke({ kunde }: { kunde: PartnerKunde }) {
  if (kunde.zustand === 'aktiv') return <Status ton="gut">aktiv</Status>
  if (kunde.zustand === 'bereitstellung') return <Status ton="wartet">wird bereitgestellt</Status>
  return <Status ton="ruhig">{kunde.zustand === 'deaktiviert' ? 'deaktiviert' : 'gelöscht'}</Status>
}

function Wert({
  name,
  children,
  achtung,
}: {
  name: string
  children: ReactNode
  achtung?: boolean
}) {
  return (
    <div>
      <dt className="text-[11.5px] text-quaternary">{name}</dt>
      <dd
        className={cx(
          'mt-0.5 font-display text-2xl leading-[1.15] font-bold tabular-nums',
          achtung ? 'text-achtung-tief' : 'text-primary',
        )}
      >
        {children}
      </dd>
    </div>
  )
}

/** Höchstens zwei Buchstaben — drei werden im Kreis zu klein. */
function kuerzel(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((teil) => teil[0])
    .join('')
    .toUpperCase()
}

/** ISO nach deutscher Schreibweise — ohne Bibliothek, es ist ein Datum. */
function datum(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('de-DE')
}
